import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { RestaurantProvider, useRestaurant } from "./context/RestaurantContext";
import { useEffect } from "react";
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import DashboardPage from "./pages/DashboardPage";
import UsersPage from "./pages/UsersPage";
import RestaurantsPage from "./pages/RestaurantsPage";
import VendorsPage from "./pages/VendorsPage";
import ItemsPage from "./pages/ItemsPage";
import CreateOrderPage from "./pages/CreateOrderPage";
import PendingOrdersPage from "./pages/PendingOrdersPage";
import FinalizedOrdersPage from "./pages/FinalizedOrdersPage";
import MatchOrderPage from "./pages/MatchOrderPage";
import OrderDetailPage from "./pages/OrderDetailPage";
import OrderHistoryPage from "./pages/OrderHistoryPage";
import ExpensesPage from "./pages/ExpensesPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import NotificationsPage from "./pages/NotificationsPage";
import "./App.css";

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, isLoading: authLoading } = useAuth();
  const { loadRestaurants, restaurants, currentRestaurant, selectRestaurant } = useRestaurant();
  
  // Load restaurants when user is authenticated
  useEffect(() => {
    if (user && restaurants.length === 0) {
      loadRestaurants();
    }
  }, [user, restaurants.length, loadRestaurants]);

  // Auto-select first restaurant if none selected
  useEffect(() => {
    if (user && restaurants.length > 0 && !currentRestaurant) {
      selectRestaurant(restaurants[0]);
    }
  }, [user, restaurants, currentRestaurant, selectRestaurant]);
  
  if (authLoading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="animate-pulse text-kale-900 font-medium">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Routes>
      {/* Public landing page — logged-in users get redirected to their dashboard */}
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute adminOnly>
          <UsersPage />
        </ProtectedRoute>
      } />
      <Route path="/restaurants" element={
        <ProtectedRoute adminOnly>
          <RestaurantsPage />
        </ProtectedRoute>
      } />
      <Route path="/vendors" element={
        <ProtectedRoute adminOnly>
          <VendorsPage />
        </ProtectedRoute>
      } />
      <Route path="/items" element={
        <ProtectedRoute adminOnly>
          <ItemsPage />
        </ProtectedRoute>
      } />
      <Route path="/create-order" element={
        <ProtectedRoute>
          <CreateOrderPage />
        </ProtectedRoute>
      } />
      <Route path="/pending-orders" element={
        <ProtectedRoute adminOnly>
          <PendingOrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/finalized-orders" element={
        <ProtectedRoute>
          <FinalizedOrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/:orderId/receive/:vendorName" element={
        <ProtectedRoute>
          <MatchOrderPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/:orderId" element={
        <ProtectedRoute>
          <OrderDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/order-history" element={
        <ProtectedRoute>
          <OrderHistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/expenses" element={
        <ProtectedRoute adminOnly>
          <ExpensesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin-settings" element={
        <ProtectedRoute adminOnly>
          <AdminSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <NotificationsPage />
        </ProtectedRoute>
      } />
      {/* Redirect old routes */}
      <Route path="/receipts" element={<Navigate to="/expenses" replace />} />
      <Route path="/analytics" element={<Navigate to="/expenses" replace />} />
      <Route path="/select-restaurant" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RestaurantProvider>
          <Toaster 
            position="top-center" 
            richColors 
            toastOptions={{
              style: {
                fontFamily: 'Manrope, sans-serif',
              },
            }}
          />
          <AppRoutes />
        </RestaurantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
